import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useFormikContext, getIn } from "formik";
import {
  TextField,
  BooleanField,
  GroupField,
  FeedbackLabel,
} from "react-invenio-forms";
import { Button, Form, Icon } from "semantic-ui-react";
import CustomArrayField from "./CustomArrayField";
import NestedVocabularyField from "./NestedVocabularyField";

const getVocabularyLabel = (item) =>
  item?.title_l10n || item?.title?.en || item?.id || "";

const emptyValueForProperty = (property) => {
  if (property?.type === "boolean") return false;
  return "";
};

export const emptyMultiLineValue = (properties = {}) => {
  if (!properties || typeof properties !== "object") return {};

  return Object.entries(properties).reduce((acc, [key, property]) => {
    if (key === "_object_name") return acc;
    acc[key] = emptyValueForProperty(property);
    return acc;
  }, {});
};

const isHiddenProperty = (property) =>
  property?.hidden === true || property?.ui_props?.hidden === true;

const isReadonlyProperty = (property) =>
  property?.readonly === true ||
  property?.ui_props?.readonly === true ||
  property?.disabled === true ||
  property?.ui_props?.disabled === true;

/**
 * Backward-compatible fallback for old single-vocabulary rows.
 * If ui_props.autofill is missing, we preserve the old behavior:
 *   schema -> schema
 *   url    -> url
 *   label  -> label
 */
const getAutofillTargets = (property, properties) => {
  const explicitAutofill = property?.autofill || property?.ui_props?.autofill;

  if (explicitAutofill && typeof explicitAutofill === "object") {
    return {
      schema: explicitAutofill.schema,
      url: explicitAutofill.url,
      label: explicitAutofill.label,
    };
  }

  return {
    schema: properties?.schema ? "schema" : null,
    url: properties?.url ? "url" : null,
    label: properties?.label ? "label" : null,
  };
};

const MultiNestedInput = ({
  fieldPath,
  label,
  placeholder,
  description,
  additionLabel,
  icon,
  required,
  properties,
}) => {
  const { values, setFieldValue, errors } = useFormikContext();
  const hasError = getIn(errors, fieldPath);

  const propertyEntries = useMemo(
    () =>
      Object.entries(properties || {}).filter(
        ([key]) => key !== "_object_name"
      ),
    [properties]
  );

  const visiblePropertyEntries = useMemo(
    () => propertyEntries.filter(([, property]) => !isHiddenProperty(property)),
    [propertyEntries]
  );

  const getChildRequired = useCallback(
    (property) => property?.required || property?.ui_props?.required || false,
    []
  );

  const getValueForLine = useCallback(
    (indexPath) => {
      const currentValue = getIn(values, fieldPath, []);
      return currentValue?.[indexPath] || emptyMultiLineValue(properties);
    },
    [values, fieldPath, properties]
  );

  const normalizeSchemaValue = (value) =>
    typeof value === "string" ? value.split("?")[0] : "";

  const clearAutofilledSiblingFields = useCallback(
    (fieldPathPrefix, property) => {
      const targets = getAutofillTargets(property, properties);

      if (targets.schema) {
        setFieldValue(`${fieldPathPrefix}.${targets.schema}`, "");
      }
      if (targets.url) {
        setFieldValue(`${fieldPathPrefix}.${targets.url}`, "");
      }
      if (targets.label) {
        setFieldValue(`${fieldPathPrefix}.${targets.label}`, "");
      }
    },
    [properties, setFieldValue]
  );

  const autofillFromVocabularySelection = useCallback(
    (fieldPathPrefix, property, item) => {
      const targets = getAutofillTargets(property, properties);

      if (!item) {
        clearAutofilledSiblingFields(fieldPathPrefix, property);
        return;
      }

      if (targets.schema) {
        setFieldValue(
          `${fieldPathPrefix}.${targets.schema}`,
          normalizeSchemaValue(
            property?.schema_value || property?.vocabulary_id || ""
          )
        );
      }

      if (targets.url) {
        setFieldValue(
          `${fieldPathPrefix}.${targets.url}`,
          item?.props?.uri || item?.links?.self || ""
        );
      }

      if (targets.label) {
        setFieldValue(
          `${fieldPathPrefix}.${targets.label}`,
          getVocabularyLabel(item)
        );
      }
    },
    [properties, setFieldValue, clearAutofilledSiblingFields]
  );

  const renderField = (propertyKey, fieldPathPrefix, lineValue, property) => {
    const fieldLabel =
      property?.label || property?.ui_props?.label || propertyKey;
    const fieldDescription =
      property?.description || property?.ui_props?.description;
    const childRequired = getChildRequired(property);
    const childFieldPath = `${fieldPathPrefix}.${propertyKey}`;
    const isReadOnly = isReadonlyProperty(property);

    if (
      (property?.type === "vocabulary" ||
        property?.type === "vocabulary_object") &&
      property?.ui_widget === "AutocompleteDropdown"
    ) {
      const isVocabObject = property?.type === "vocabulary_object";
      const vocabObjectValue = isVocabObject
        ? getIn(values, childFieldPath, null)
        : null;
      const hasVocabObjectValue =
        vocabObjectValue &&
        typeof vocabObjectValue === "object" &&
        vocabObjectValue.id;

      return (
        <Form.Field key={childFieldPath} className="sixteen wide">
          <NestedVocabularyField
            fieldPath={childFieldPath}
            property={property}
            label={fieldLabel}
            required={childRequired}
            onSelectedItem={
              isVocabObject
                ? undefined
                : (item) =>
                    autofillFromVocabularySelection(
                      fieldPathPrefix,
                      property,
                      item
                    )
            }
          />
          {hasVocabObjectValue && (
            <>
              <Form.Group widths="equal" style={{ marginTop: "0.5rem" }}>
                <Form.Input
                  id={`${childFieldPath}__display_id`}
                  label="ID"
                  readOnly
                  value={vocabObjectValue.id || ""}
                  fluid
                  className="vocabulary-object-readonly-field"
                />
                <Form.Input
                  id={`${childFieldPath}__display_label`}
                  label="Label"
                  readOnly
                  value={vocabObjectValue.label || ""}
                  fluid
                  className="vocabulary-object-readonly-field"
                />
              </Form.Group>
              <Form.Group widths="equal">
                <Form.Input
                  id={`${childFieldPath}__display_schema`}
                  label="Schema"
                  readOnly
                  value={vocabObjectValue.schema || ""}
                  fluid
                  className="vocabulary-object-readonly-field"
                />
                <Form.Input
                  id={`${childFieldPath}__display_url`}
                  label="URL"
                  readOnly
                  value={vocabObjectValue.url || ""}
                  fluid
                  className="vocabulary-object-readonly-field"
                />
              </Form.Group>
            </>
          )}
          {fieldDescription && (
            <label className="helptext sixteen wide field">
              {fieldDescription}
            </label>
          )}
        </Form.Field>
      );
    }

    if (property?.type === "vocabulary" && property?.ui_widget === "Dropdown") {
      const dropdownOptions = (property.options || []).map((o) => ({
        key: o.id,
        value: o.id,
        text: o.title_l10n || o.id,
      }));
      return (
        <React.Fragment key={childFieldPath}>
          <Form.Select
            className="sixteen wide"
            id={childFieldPath}
            label={fieldLabel}
            required={childRequired}
            options={dropdownOptions}
            value={lineValue[propertyKey] ?? ""}
            onChange={(e, { value }) => setFieldValue(childFieldPath, value)}
            clearable={property?.clearable ?? true}
            placeholder={property?.placeholder || `Select ${fieldLabel}`}
            disabled={isReadOnly}
            search
          />
          {fieldDescription && (
            <label className="helptext sixteen wide field">
              {fieldDescription}
            </label>
          )}
        </React.Fragment>
      );
    }

    if (property?.type === "boolean") {
      return (
        <Form.Field key={propertyKey} className="sixteen wide">
          <BooleanField
            fieldPath={childFieldPath}
            label={fieldLabel}
            required={childRequired}
            disabled={isReadOnly}
          />
          {fieldDescription && (
            <label className="helptext sixteen wide field">
              {fieldDescription}
            </label>
          )}
        </Form.Field>
      );
    }

    return (
      <Form.Field key={propertyKey} className="sixteen wide">
        <TextField
          fieldPath={childFieldPath}
          label={fieldLabel}
          required={childRequired}
          placeholder={
            property?.placeholder ||
            property?.ui_props?.placeholder ||
            placeholder ||
            `Enter ${fieldLabel}`
          }
          value={lineValue[propertyKey] ?? ""}
          disabled={isReadOnly}
          onChange={
            isReadOnly
              ? undefined
              : (e) => setFieldValue(childFieldPath, e.target.value)
          }
        />
        {fieldDescription && (
          <label className="helptext sixteen wide field">
            {fieldDescription}
          </label>
        )}
      </Form.Field>
    );
  };

  return (
    <div className={hasError ? "error field" : ""}>
      <CustomArrayField
        fieldPath={fieldPath}
        label={label}
        labelIcon={icon}
        addButtonLabel={additionLabel || "Add Line"}
        defaultNewValue={emptyMultiLineValue(properties)}
        helpText={description}
      >
        {({ arrayHelpers, indexPath }) => {
          const fieldPathPrefix = `${fieldPath}.${indexPath}`;
          const lineValue = getValueForLine(indexPath);

          return (
            <GroupField optimized key={fieldPathPrefix}>
              <div style={{ fontWeight: "bold", marginBottom: "10px" }}>
                {properties._object_name || label || "Object"}
              </div>

              <div className="sixteen wide field flow-wrap nested-indentation">
                {visiblePropertyEntries.map(([propertyKey, property]) =>
                  renderField(propertyKey, fieldPathPrefix, lineValue, property)
                )}
              </div>

              <Form.Field style={{ marginTop: "1.75rem", float: "right" }}>
                <Button
                  aria-label="Remove line"
                  className="close-btn right"
                  icon
                  type="button"
                  onClick={() => arrayHelpers.remove(indexPath)}
                >
                  <Icon name="close" />
                </Button>
              </Form.Field>
            </GroupField>
          );
        }}
      </CustomArrayField>

      {hasError && <FeedbackLabel pointing="above" fieldPath={fieldPath} />}
    </div>
  );
};

MultiNestedInput.propTypes = {
  fieldPath: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  description: PropTypes.string,
  additionLabel: PropTypes.string,
  icon: PropTypes.string,
  required: PropTypes.bool,
  properties: PropTypes.object.isRequired,
};

MultiNestedInput.defaultProps = {
  additionLabel: "Add Line",
  icon: undefined,
  required: false,
  description: undefined,
  placeholder: undefined,
};

export default MultiNestedInput;
